import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from "react-router-dom";
import { AppShell } from "./app/AppShell";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { Progress } from "./components/Notice";
import { Account } from "./screens/Account";
import { AuthCallback } from "./screens/AuthCallback";
import { Create } from "./screens/Create";
import { Dealer } from "./screens/Dealer";
import { Health } from "./screens/Health";
import { Library } from "./screens/Library";
import { RecoverPassword } from "./screens/RecoverPassword";
import { SignIn, type Audience } from "./screens/SignIn";
import "./styles.css";

function RequireSession({ signin = "/signin" }: { signin?: string }) {
  const { session, recovery } = useAuth();
  const location = useLocation();
  if (session === undefined) return <Progress label="Checking your session…" />;
  // A reset link signs the user in; until the new password is set, that
  // session only opens the recovery screen.
  if (session && recovery) return <Navigate to={signin === "/dealer/signin" ? "/auth/recover?audience=dealer" : "/auth/recover"} replace />;
  if (!session) return <Navigate to={signin} replace state={{ from: location.pathname + location.search }} />;
  return <Outlet />;
}

const AUTH_PATHS = new Set(["/signin", "/signup", "/dealer/signin"]);

function SignedOutOnly({ mode = "signin", audience = "client" }: { mode?: "signin" | "signup"; audience?: Audience }) {
  const { session, recovery } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const home = audience === "dealer" ? "/dealer" : "/";
  if (session && recovery) return <Navigate to={audience === "dealer" ? "/auth/recover?audience=dealer" : "/auth/recover"} replace />;
  if (session) return <Navigate to={from && !AUTH_PATHS.has(from) ? from : home} replace />;
  return <SignIn initialMode={mode} audience={audience} />;
}

const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      { path: "/signin", element: <SignedOutOnly /> },
      { path: "/signup", element: <SignedOutOnly mode="signup" /> },
      { path: "/dealer/signin", element: <SignedOutOnly audience="dealer" /> },
      { path: "/auth/callback", element: <AuthCallback /> },
      { path: "/auth/recover", element: <RecoverPassword /> },
      { path: "/ops/health", element: <Health /> },
      {
        element: <RequireSession />,
        children: [
          { path: "/", element: <Create /> },
          { path: "/library", element: <Library /> },
          { path: "/account", element: <Account /> },
          { path: "/link/sonos", element: <Account /> },
        ],
      },
      {
        element: <RequireSession signin="/dealer/signin" />,
        children: [{ path: "/dealer", element: <Dealer /> }],
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  </React.StrictMode>,
);
